import type { AuthResponse, SocialUser } from '@moba/shared';
import { defaultEndpoint } from './NetClient.ts';

import { t, tr } from './i18n.ts';
/**
 * Sesión de la cuenta del jugador (registro, inicio y cierre de sesión por HTTP
 * contra el servidor de juego). El token se guarda en `localStorage` para no
 * tener que iniciar sesión en cada visita; sin cuenta se juega como invitado.
 */

const TOKEN_KEY = 'moba.session';

/** Base HTTP del servidor de juego (mismo host/puerto que el WebSocket). */
export function httpEndpoint(): string {
  return defaultEndpoint().replace(/^ws/, 'http');
}

export class AccountClient {
  user: SocialUser | null = null;
  token = '';
  private readonly listeners = new Set<() => void>();

  constructor(private readonly base = httpEndpoint()) {
    try {
      this.token = localStorage.getItem(TOKEN_KEY) ?? '';
    } catch {
      this.token = '';
    }
  }

  get loggedIn(): boolean {
    return Boolean(this.user && this.token);
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private setSession(token: string, user: SocialUser | null): void {
    this.token = token;
    this.user = user;
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      // sin almacenamiento: la sesión dura lo que la pestaña
    }
    this.emit();
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${this.base}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          ...(init.headers ?? {}),
        },
      });
    } catch {
      throw new Error(t('No se pudo conectar con el servidor'));
    }
    const data = (await response.json().catch(() => ({}))) as T & { error?: string };
    if (!response.ok) throw Object.assign(new Error(data.error ? tr(data.error) : `Error ${response.status}`), { status: response.status });
    return data;
  }

  /** Recupera la sesión guardada (si sigue siendo válida). */
  async restore(): Promise<void> {
    if (!this.token) return;
    try {
      const { user } = await this.request<{ user: SocialUser }>('/api/me');
      this.setSession(this.token, user);
    } catch (error) {
      // 401 = caducó; sin conexión se conserva el token para reintentar luego
      if ((error as { status?: number }).status === 401) this.setSession('', null);
    }
  }

  async login(username: string, password: string): Promise<void> {
    const data = await this.request<AuthResponse>('/api/login', { method: 'POST', body: JSON.stringify({ username, password }) });
    this.setSession(data.token, data.user);
  }

  async register(username: string, password: string): Promise<void> {
    const data = await this.request<AuthResponse>('/api/register', { method: 'POST', body: JSON.stringify({ username, password }) });
    this.setSession(data.token, data.user);
  }

  async logout(): Promise<void> {
    const token = this.token;
    this.setSession('', null);
    if (!token) return;
    await fetch(`${this.base}/api/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }

  /** El servidor rechazó la sesión (p. ej. en la sala social): se cierra aquí también. */
  expire(): void {
    if (this.token || this.user) this.setSession('', null);
  }
}
